/* global module, require */
const Style = require('./Style'),
	THREE = require('three'),
	asBuffer = require('../asBuffer'),
	createDocument = require('../createDocument'),
	firstOption = require('../firstOption'),
	gClampGeo = require('../geo/clamp'),
	{
		getGlassThickness,
		getGlassType,
		glassCT,
		glassCTL,
		glassDefault,
		makeGlass,
	} = require('../data/glass'),
	img = require('../../res/planner-post.png'),
	imgBalustrade = require('../../res/planner-post-balustrade.png'),
	makeTopRail = require('../func/makeTopRail'),
	steel = require('../mat/steel'),
	unit = require('../unit');

const capDomed = 'Domed',
	capFlat = 'Flat',
	capRadius = 'Radius';

const gGlassThicknesses = {
	[glassCT]: { '8mm': 8, '10mm': 10, '12mm': 12 },
	[glassCTL]: { '8.76mm': 8.76, '10.76mm': 10.76, '12.76mm': 12.76 },
};

const gTopRailThicknesses = {
	42.4: { '42.4mm⌀': 42.4 },
	48.3: { '42.4mm⌀': 42.4, '48.3mm⌀': 48.3 },
};

/* unknown dimensions */

const gBaseHeight = unit.mm(10),
	gBaseRadius = unit.mm(60),
	gClampDistance = unit.mm(100),
	gClampOffset = 0.005,
	gConnectorHeight = unit.mm(78),
	gConnectorThickness = unit.mm(10),
	gEndDomedSize = unit.mm(21.2),
	gEndRadiusSize = unit.mm(7),
	gGlassGap = unit.mm(33),
	gMaxGlassLength = unit.m(1.2),
	gTopClearance = 0;

const gBaseGeo = new THREE.CylinderGeometry(
	gBaseRadius,
	gBaseRadius,
	gBaseHeight
);
const gBaseMesh = new THREE.Mesh(gBaseGeo);
gBaseMesh.matrix.makeTranslation(0, gBaseHeight / 2, 0);

const gConnectorGeo = new THREE.CylinderGeometry(
	gConnectorThickness,
	gConnectorThickness,
	gConnectorHeight
);

function PostSystem(planner, args) {
	this.args = args;

	if (args.balustrade) {
		this.balustrade = true;
		this.name = 'Post System\nwith Top Rail';
		this.image = imgBalustrade;
	} else {
		this.balustrade = false;
		this.name = 'Post System\nno Top Rail';
		this.image = img;
	}

	this.planner = planner;
	Style.apply(this);
}
PostSystem.prototype = new Style();

PostSystem.prototype.getConfig = function() {
	var config = {
		installation: {
			name: 'Installation Type',
			options: {
				Inside: 'Inside',
				Outside: 'Outside',
				Coastal: 'Coastal',
			},
			info:
				'System can be used for internal, external and coastal. The posts are calculated that a centre line distance between posts can be up to a maximum of 1200mm.',
		},
		glassType: getGlassType(
			createDocument(null, [
				'The Glazing panels can be supplied in two forms.',
				{ _tag: 'h4', innerText: 'Toughened Glazing' },
				'Known as tempered glass this can be upto 5x stronger than normal float glass, a single panel of glass.',
				'Our standard thicknesses offered are 8.0mm, 10.0mm, and 12.0mm and are chosen as a result of differences between post optimisation, As standard we recommend 10.0mm.',
				{ _tag: 'h4', innerText: 'Toughened Laminate Glazing' },
				'Laminated glass has the same strength as regular glass but is fundamentally 2 panels with a plastic interlayer. The interlayer acts as a resin which holds the shards in place should the glass break.',
				'Our standard thicknesses are 8.76mm, 10.76mm and 12.76mm.',
			])
		),
		glassThickness: getGlassThickness(
			this.options.glassType,
			gGlassThicknesses
		),
		poleThickness: {
			name: 'Upright Post Detail',
			options: {
				'42.4⌀ x 2.0mm wall': 42.4,
				'48.3⌀ x 2.0mm wall': 48.3,
			},
			transform: parseFloat,
			info:
				'Available in both 42⌀ and 48⌀ stainless steel posts complete with base cover, glass clamps and post tops.',
		},
		poleHeight: {
			name: 'Post Height',
			range: { min: 800, max: 1200 },
			suffix: 'mm',
			info:
				'Post height has a range of 800mm to 1200mm based on the finished floor level to the height of the glazing. Care must be taken when giving a height requirement. For building control purposes 1100mm is a minimum requirement and we recommend this as the standard height provided.',
		},
		bottomClearance: {
			name: 'Bottom Clearance',
			range: { min: 30, max: 99 },
			suffix: 'mm',
			info:
				'The bottom clearance is the gap between the finished floor level and the underside of the glazing panel. With the height of the cover plate being 25mm in height the range finder starts from 30mm (5mm clearance) and increases to 99mm. 99mm is the maximum achievable gap to adhere to building regulations. As standard we would recommend a minimum clearance of 30mm.',
		},
	};

	if (this.balustrade) {
		config.topRail = {
			name: 'Top Rail Detail',
			options: gTopRailThicknesses[this.options.poleThickness],
			transform: parseFloat,
			info: createDocument(null, [
				'It is normally customary to have the same top rail detail as the post detail but with Easyfix Railplus you can have the following options:',
				{
					_tag: 'table',
					_children: [
						{
							_tag: 'thead',
							_children: [
								{
									_tag: 'tr',
									_children: [
										{ _tag: 'th', innerText: 'Post Detail' },
										{ _tag: 'th', innerText: 'Top Rail Detail' },
									],
								},
							],
						},
						{
							_tag: 'tbody',
							_children: [
								{
									_tag: 'tr',
									_children: [
										{ _tag: 'td', innerText: '42.4⌀ x 2.0' },
										{ _tag: 'td', innerText: '42.4⌀ x 2.0' },
									],
								},
								{
									_tag: 'tr',
									_children: [
										{ _tag: 'td', innerText: '48.3⌀ x 2.0' },
										{ _tag: 'td', innerText: '42.4⌀ x 2.0 or 48.3⌀ x 2.0' },
									],
								},
							],
						},
					],
				},
			]),
		};
	} else {
		config.endCap = {
			name: 'Post Top Detail',
			options: { Flat: capFlat, Radius: capRadius, Domed: capDomed },
			info:
				'As there is no top rail, the post needs to be capped to avoid injury. This is purely a matter of choice: Flat, Radius and Domed. The radius end cap has a slight rise of approximately 5mm and the Domed end cap has a rise of half the diameter 21mm for example when using the 42.4⌀ post size.',
		};
	}

	return config;
};

PostSystem.prototype.setOptions = function(options) {
	const oldGlassType = this.options.glassType,
		oldPoleThickness = this.options.poleThickness;
	var configChange = false;

	this.options = {
		installation: 'Inside',
		glassType: glassDefault,
		glassThickness: 10,
		poleHeight: 1100,
		poleThickness: 42.4,
		bottomClearance: 50,
		topRail: 42.4,
		endCap: capFlat,
		...options,
	};

	if (oldGlassType !== this.options.glassType) {
		this.options.glassThickness = firstOption(
			gGlassThicknesses[this.options.glassType]
		);
		configChange = true;
	}

	if (oldPoleThickness !== this.options.poleThickness) {
		this.options.topRail = firstOption(
			gTopRailThicknesses[this.options.poleThickness]
		);
		configChange = true;
	}

	return configChange;
};

PostSystem.prototype.addPole = function(position) {
	const geo = new THREE.Geometry(),
		endCap = this.options.endCap,
		poleHeight = unit.mm(this.options.poleHeight),
		poleThickness = unit.mm(this.options.poleThickness);

	const poleMesh = makePole(poleThickness, poleHeight);
	geo.merge(poleMesh.geometry, poleMesh.matrix);

	geo.merge(gBaseMesh.geometry, gBaseMesh.matrix);

	if (this.balustrade) {
		const topMesh = new THREE.Mesh(gConnectorGeo);
		topMesh.matrix.makeTranslation(
			0,
			poleHeight + gBaseHeight + gConnectorHeight / 2,
			0
		);

		geo.merge(topMesh.geometry, topMesh.matrix);
	} else {
		const radius = poleThickness / 2;
		var endGeo,
			endMatrix = new THREE.Matrix4();

		if (endCap === capDomed) {
			endGeo = new THREE.SphereGeometry(gEndDomedSize).scale(
				radius / gEndDomedSize,
				1,
				radius / gEndDomedSize
			);
		} else if (endCap === capRadius) {
			endGeo = new THREE.SphereGeometry(gEndRadiusSize).scale(
				radius / gEndRadiusSize,
				1,
				radius / gEndRadiusSize
			);
		}

		if (endGeo) {
			endMatrix.makeTranslation(0, poleHeight + gBaseHeight, 0);
			geo.merge(endGeo, endMatrix);
		}
	}

	const mesh = new THREE.Mesh(asBuffer(geo), steel);
	mesh.position.copy(position);

	return mesh;
};

PostSystem.prototype.connect = function(a, b, sectionIndex) {
	const bottomClearance = unit.mm(this.options.bottomClearance),
		glassThickness = unit.mm(this.options.glassThickness),
		glassType = this.options.glassType,
		group = new THREE.Group(),
		poleHeight = unit.mm(this.options.poleHeight),
		poleThickness = unit.mm(this.options.poleThickness),
		topRail = unit.mm(this.options.topRail);
	const between = new THREE.Vector3().subVectors(b, a),
		sectionCount = Math.ceil(between.length() / gMaxGlassLength),
		sectionVec = new THREE.Vector3().copy(between).divideScalar(sectionCount),
		sectionVecReversed = new THREE.Vector3()
			.copy(sectionVec)
			.multiplyScalar(-1);
	var position = new THREE.Vector3().copy(a);

	if (sectionIndex === 1) group.add(this.addPole(b));
	group.add(this.addPole(position));

	for (var i = 0; i < sectionCount; i++) {
		const next = new THREE.Vector3().copy(position).add(sectionVec);

		if (i < sectionCount - 1) {
			group.add(this.addPole(next));
		}

		group.add(
			makeGlass(glassType, {
				between: sectionVec,
				gap: gGlassGap * 2,
				height: poleHeight - bottomClearance - gTopClearance,
				offset: -gBaseHeight - bottomClearance,
				start: position,
				thickness: glassThickness,
			})
		);

		group.add(
			makeClamp({
				position,
				between: sectionVec,
				poleThickness,
				y: bottomClearance + gClampDistance + gBaseHeight,
			})
		);
		group.add(
			makeClamp({
				position,
				between: sectionVec,
				poleThickness,
				y: gBaseHeight + poleHeight - gTopClearance - gClampDistance,
			})
		);
		group.add(
			makeClamp({
				position: next,
				between: sectionVecReversed,
				poleThickness,
				y: bottomClearance + gClampDistance + gBaseHeight,
			})
		);
		group.add(
			makeClamp({
				position: next,
				between: sectionVecReversed,
				poleThickness,
				y: gBaseHeight + poleHeight - gTopClearance - gClampDistance,
			})
		);

		if (this.balustrade)
			group.add(
				makeTopRail({
					start: position,
					between: sectionVec,
					radius: topRail / 2,
					height: gBaseHeight + gConnectorHeight + poleHeight,
				})
			);

		position = next;
	}

	group.add(this.planner.call('makeSectionLabel', a, b, sectionIndex - 1));
	this.group.add(group);
};

function makePole(thickness, height) {
	const geo = new THREE.CylinderGeometry(thickness / 2, thickness / 2, height);
	const mesh = new THREE.Mesh(geo);
	mesh.matrix.makeTranslation(0, height / 2 + gBaseHeight, 0);

	return mesh;
}

function makeClamp({ position, between, poleThickness, y }) {
	const mesh = new THREE.Mesh(gClampGeo, steel);
	const dir = new THREE.Vector3()
		.copy(between)
		.normalize()
		.multiplyScalar(poleThickness * gClampOffset);

	mesh.rotation.x = Math.PI / 2;
	mesh.rotation.z = (3 * Math.PI) / 2 - Math.atan2(dir.x, dir.z);

	mesh.position.copy(position).add(dir);
	mesh.position.y = y;

	return mesh;
}

module.exports = PostSystem;
